import api from './axiosApi'

const normalizedFallbackDomain = (fallbackDomain: string | undefined): string | null => {
  const domain = fallbackDomain?.trim()

  if (!domain) {
    return null
  }

  try {
    const url = new URL(/^https?:\/\//i.test(domain) ? domain : `https://${domain}`)
    return url.hostname || null
  } catch {
    return null
  }
}

export type RoverPickSession = { url: string; origin: string; nonce: string; expiresAt: string }

export async function createRoverPickSession(teamPid: string | number, targetUrl: string): Promise<RoverPickSession> {
  const { data } = await api.post<unknown>(`/api/${teamPid}/rover/sessions`, { url: targetUrl, mode: 'pick' })
  if (
    typeof data !== 'object' ||
    data === null ||
    !('url' in data) ||
    typeof data.url !== 'string' ||
    !('origin' in data) ||
    typeof data.origin !== 'string' ||
    !('nonce' in data) ||
    typeof data.nonce !== 'string' ||
    !data.nonce ||
    !('expiresAt' in data) ||
    typeof data.expiresAt !== 'string' ||
    !Number.isFinite(Date.parse(data.expiresAt))
  ) {
    throw new Error('Invalid picker session.')
  }
  const url = new URL(data.url)
  const origin = new URL(data.origin)
  if (
    !['https:', 'http:'].includes(url.protocol) ||
    url.origin !== data.origin ||
    origin.hostname.split('.').length < 3 ||
    url.username ||
    url.password
  ) {
    throw new Error('Invalid picker session.')
  }
  return { url: data.url, origin: data.origin, nonce: data.nonce, expiresAt: data.expiresAt }
}

export const resolveRoverTargetUrl = (pageUrl: string, fallbackDomain: string | undefined): string | null => {
  const page = pageUrl.trim()
  const fallbackHost = normalizedFallbackDomain(fallbackDomain)

  if (!page) {
    return null
  }

  if (page === 'all pages' || page === 'homepage') {
    return fallbackHost ? `https://${fallbackHost}/` : null
  }

  try {
    const url = new URL(/^[a-z][a-z\d+.-]*:\/\//i.test(page) ? page : `https://${page}`)

    if (!url.hostname) {
      return null
    }

    if (decodeURIComponent(url.hostname) === '*') {
      if (!fallbackHost) {
        return null
      }
      url.hostname = fallbackHost
    }

    url.pathname = url.pathname.replace(/\*+$/, '')
    return url.toString()
  } catch {
    return null
  }
}

export type NewPageUrlResult = { url: string; error?: undefined } | { url?: undefined; error: string }

/**
 * A page URL typed by the user to open in the visual editor. Like campaign creation (`z.string().url()`), any
 * http(s) URL is accepted, not only the team's configured domains; a bare `example.com/page` gets `https://`.
 */
export const normalizeNewPageUrl = (input: string): NewPageUrlResult => {
  const value = input.trim()
  if (!value) {
    return { error: 'Enter the URL of the page to personalize.' }
  }
  const hasScheme = /^[a-z][a-z\d+.-]*:/i.test(value) && !/^[^/:?#]+:\d+(?:[/?#]|$)/.test(value)
  // A scheme-less single word ("pricing") is a typo, not a host; qualified http(s) URLs keep URL's own acceptance.
  const bareHost = hasScheme ? null : (value.split(/[/?#:]/, 1)[0] ?? '')
  if (
    bareHost !== null &&
    !bareHost.includes('.') &&
    !['localhost', '*'].includes(bareHost) &&
    !value.startsWith('[')
  ) {
    return { error: 'Enter a valid URL, like https://example.com/pricing.' }
  }
  let url: URL
  try {
    url = new URL(hasScheme ? value : `https://${value}`)
  } catch {
    return { error: 'Enter a valid URL, like https://example.com/pricing.' }
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return { error: 'Only http:// and https:// pages can be personalized.' }
  }
  return { url: url.toString() }
}
